import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createDashboard, deleteDashboard, getDashboard, listDashboards } from "../../api/kpi.api";
import KpiBadge from "../../components/kpi/KpiBadge";
import KpiTrend from "../../components/kpi/KpiTrend";
import { listKpis } from "../../api/kpi.api";
import { KPICopy } from "../../copy/kpiCopy";
import { useAuth } from "../../useAuth";
import { canEditKpis, canManageKpis } from "../../accessControl";
import Button from "../../ui/Button";
import PageState from "../../ui/PageState";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import { formatKpiValueWithUnit } from "../../utils/kpiUnits";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardStyle,
} from "../../ui/pagePatterns";

export default function KpiDashboard() {
  const [dashboards, setDashboards] = useState([]);
  const [selected, setSelected] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const confirmDashboard = dashboards.find((dashboard) => dashboard.id === confirmDeleteId);
  const navigate = useNavigate();
  const { user } = useAuth() || {};

  const canEdit = useMemo(() => canEditKpis(user), [user]);
  const canManage = useMemo(() => canManageKpis(user), [user]);

  const load = () => {
    setLoadError("");
    listDashboards()
      .then((res) => setDashboards(res.data || []))
      .catch((err) => {
        setDashboards([]);
        setLoadError(err?.response?.data?.detail || err?.message || "Unable to load KPI dashboards.");
      });
    listKpis()
      .then((res) => setKpis(res.data || []))
      .catch((err) => {
        setKpis([]);
        setLoadError((current) => current || err?.response?.data?.detail || err?.message || "Unable to load KPI records.");
      });
  };

  useEffect(() => {
    load();
  }, []);

  const selectDashboard = async (id) => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await getDashboard(id);
      setSelected(res.data);
      // For now render all KPIs if no items
      const items = res.data.items?.length ? res.data.items : kpis.map((k) => ({ id: `k${k.id}`, kpi_id: k.id }));
      setWidgets(items);
    } catch (err) {
      setSelected(null);
      setWidgets([]);
      setLoadError(err?.response?.data?.detail || err?.message || "Unable to load the selected dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!canEdit) return;
    const res = await createDashboard({ name: "KPI Dashboard", description: "Auto-created dashboard", items: [] });
    load();
    selectDashboard(res.data.id);
  };

  const handleDelete = async (id) => {
    if (!canManage) return;
    await deleteDashboard(id);
    setSelected(null);
    setWidgets([]);
    load();
  };

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderStyle}>
        <div style={pageHeaderTitleBlockStyle}>
          <h2 style={pageTitleStyle}>{KPICopy.dashboardTitle}</h2>
          <p style={pageSubtitleStyle}>{KPICopy.dashboardBanner}</p>
        </div>
        <div style={pageHeaderActionsStyle}>
          <Button
            onClick={handleCreate}
            disabled={!canEdit}
            title={canEdit ? undefined : "Read-only access"}
            variant="primary"
          >
            New dashboard
          </Button>
        </div>
      </div>
      {!canEdit && (
        <div style={{ color: colors.textMuted, fontSize: typography.size.xs }}>
          {KPICopy.readOnlyNotice}
        </div>
      )}

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        {dashboards.map((d) => (
          <div
            key={d.id}
            onClick={() => selectDashboard(d.id)}
            style={{
              ...surfaceCardStyle,
              padding: spacing.xl,
              borderRadius: radius.md,
              border: selected?.id === d.id ? `1px solid ${colors.accent}` : `1px solid ${colors.border}`,
              boxShadow: selected?.id === d.id ? `0 0 0 3px ${colors.focusRing}` : shadows.xs,
              cursor: "pointer",
              minWidth: "200px",
            }}
          >
            <div style={{ fontWeight: 700 }}>{d.name}</div>
            <div style={{ color: colors.textMuted, fontSize: typography.size.xs }}>{d.description}</div>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDeleteId(d.id);
              }}
              disabled={!canManage}
              title={canManage ? undefined : "Manage access required"}
              variant="destructive"
              style={{
                marginTop: spacing.sm,
              }}
            >
              Delete
            </Button>
          </div>
        ))}
      </div>
      {loadError ? (
        <PageState
          state="error"
          align="left"
          title="KPI dashboards are unavailable"
          description={`${loadError} Refresh the workspace to try again.`}
          action={
            <Button type="button" variant="secondary" onClick={load}>
              Refresh
            </Button>
          }
        />
      ) : dashboards.length === 0 && !loading && !selected ? (
        <PageState
          state="empty"
          align="left"
          title="No KPI dashboards yet"
          description="Create a dashboard to group the KPI signals that matter most to your team."
          action={
            <Button
              onClick={handleCreate}
              disabled={!canEdit}
              title={canEdit ? undefined : "Read-only access"}
              variant="primary"
            >
              New dashboard
            </Button>
          }
        />
      ) : null}

      {!loadError && loading ? (
        <PageState
          state="loading"
          align="left"
          title="Loading dashboard"
          description="Glyder is preparing the selected KPI dashboard and its current widgets."
        />
      ) : !loadError && !selected ? (
        dashboards.length > 0 ? (
          <PageState
            state="empty"
            align="left"
            title="Select a dashboard"
            description="Choose an existing dashboard to review grouped KPI signals and linked metrics."
          />
        ) : null
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "12px" }}>
          {widgets.map((item) => {
            const kpi = kpis.find((k) => k.id === item.kpi_id);
            if (!kpi) return null;
            return (
              <div
                key={item.id || item.kpi_id}
                style={{ ...surfaceCardStyle, padding: spacing.xl, borderRadius: radius.md }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{kpi.name}</div>
                    <div style={{ color: colors.textMuted, fontSize: typography.size.xs }}>{kpi.category || "General"}</div>
                  </div>
                  <KpiBadge value={kpi.latest_rag} />
                </div>
                <div style={{ marginTop: "8px", fontSize: "24px", fontWeight: 700, color: colors.textPrimary }}>
                  {kpi.latest_value !== null && kpi.latest_value !== undefined
                    ? formatKpiValueWithUnit(kpi.latest_value, kpi.unit)
                    : "--"}
                </div>
                <div style={{ marginTop: "8px" }}>
                  {kpi.measurement_count && kpi.measurement_count > 1 ? (
                    <KpiTrend points={[kpi.latest_value || 0]} />
                  ) : (
                    <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Add more measurements to show a trend.</div>
                  )}
                </div>
                <Button
                  onClick={() => navigate(`/kpis/${kpi.id}`)}
                  variant="secondary"
                  style={{ marginTop: "10px", alignSelf: "flex-start" }}
                >
                  Open KPI
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <DestructiveConfirm
        actionId="KPI_DASHBOARD_DELETE"
        open={Boolean(confirmDeleteId)}
        resourceDisplayName={confirmDashboard?.name}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={async () => {
          if (!confirmDeleteId) return;
          const targetId = confirmDeleteId;
          setConfirmDeleteId(null);
          await handleDelete(targetId);
        }}
      />
    </div>
  );
}
